import { Accordion, AccordionDetails, AccordionSummary, Box, FormControlLabel, TextField, TextFieldProps } from '@mui/material';
import { WO2Checkbox } from 'src/common';
import { BaseReportComponentProps, MdaReportTypes, YearType } from '../../store/types';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React, { useEffect } from 'react';
import { DateRange } from 'src/store';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';
import { DateTime } from 'luxon';
import { ExpandMore } from '@mui/icons-material';
import { YearTypeSelector } from '../YearTypeSelector';

export interface TaxSummaryProps extends BaseReportComponentProps {
  setTaxSummarySelectedParameter: ActionCreatorWithPayload<boolean, string>;
  setDateRangeTaxSummaryParameter: ActionCreatorWithPayload<DateRange>;
  setTaxSummaryYearTypeParameter: ActionCreatorWithPayload<YearType>;
  expandTaxSummary: () => void;
}

export const TaxSummary = ({
  parameters,
  setTaxSummarySelectedParameter,
  setDateRangeTaxSummaryParameter,
  setTaxSummaryYearTypeParameter,
  expandTaxSummary,
  createReportSortRequest,
}: TaxSummaryProps) => {
  const handleClickTaxSummarySelect = () => {
    setTaxSummarySelectedParameter(!parameters.selectedReports?.taxSummary.selected);
    createReportSortRequest(!parameters.selectedReports?.taxSummary.selected, MdaReportTypes.TaxSummary.id, MdaReportTypes.TaxSummary.displayName);
  };

  const updateTaxSummaryDateRange = (date: DateTime, yearType: YearType) => {
    if (!date || !date.isValid) return;

    const year = date.year;

    const dateRange: DateRange =
      yearType === YearType.FinancialYear
        ? {
            dateFrom: DateTime.fromObject({ year: year - 1, month: 7, day: 1 }).toISODate(),
            dateTo: DateTime.fromObject({ year, month: 6, day: 30 }).toISODate(),
          }
        : {
            dateFrom: DateTime.fromObject({ year, month: 1, day: 1 }).toISODate(),
            dateTo: DateTime.fromObject({ year, month: 12, day: 31 }).toISODate(),
          };
    setDateRangeTaxSummaryParameter(dateRange);
  };

  const handleClickDateForTaxSummary = (date: DateTime | null) => {
    if (!date || !date.isValid) return;

    updateTaxSummaryDateRange(date, parameters.selectedReports.taxSummary.yearType);
  };

  useEffect(() => {
    const { toDate, yearType } = parameters.selectedReports.taxSummary;

    if (!toDate) return;

    const date = DateTime.fromISO(toDate);
    if (!date.isValid) return;

    updateTaxSummaryDateRange(date, yearType);
  }, [parameters.selectedReports.taxSummary.yearType]);

  return (
    <Accordion onChange={expandTaxSummary} expanded={parameters.selectedReports?.taxSummary.expanded || false} elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FormControlLabel
          sx={{ flex: 1 }}
          aria-label="Acknowledge"
          onClick={(event) => event.stopPropagation()}
          onFocus={(event) => event.stopPropagation()}
          control={
            <WO2Checkbox color="primary" checked={parameters.selectedReports?.taxSummary.selected} onChangeHandler={handleClickTaxSummarySelect} />
          }
          label="Tax Summary"
        />

        <div onClick={(e) => e.stopPropagation()}>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DatePicker
              label={parameters.selectedReports.taxSummary.yearType.toString()}
              views={['year']}
              value={parameters.selectedReports.taxSummary.toDate ? DateTime.fromISO(parameters.selectedReports.taxSummary.toDate) : null}
              onChange={handleClickDateForTaxSummary}
              minDate={DateTime.fromObject({ year: 2000 })}
              maxDate={DateTime.fromObject({ year: DateTime.now().year, month: 12, day: 31 })}
              renderInput={(props: TextFieldProps) => <TextField sx={{ minWidth: 194, maxWidth: 194, mr: 1 }} {...props} />}
            />
          </LocalizationProvider>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <Box display="flex" gap={2} flexDirection="column">
          <YearTypeSelector
            value={parameters.selectedReports.taxSummary.yearType}
            setValue={(value: YearType) => setTaxSummaryYearTypeParameter(value)}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
